import { type ReactElement, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fontFamilies, s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

export type ProjectDataWipeSectionProps = {
  project: ProjectDto | null;
  deleteProject: {
    isPending: boolean;
    mutate: (
      input: { id: string },
      options?: {
        onSuccess?: () => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  wipeProjectData: {
    isPending: boolean;
    mutate: (
      input: {
        id: string;
        confirmation: string;
        reasonCode?: string;
        ticket?: string;
        async?: boolean;
      },
      options?: {
        onSuccess?: (result: { status: 'wiped' | 'scheduled'; jobId?: string }) => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  onProjectDeleted: () => void;
};

export const ProjectDataWipeSection = ({
  project,
  deleteProject,
  wipeProjectData,
  mutationError,
  notify,
  onProjectDeleted,
}: ProjectDataWipeSectionProps): ReactElement => {
  const projectNameCodeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: fontFamilies.mono,
    backgroundColor: 'action.hover',
    borderRadius: s('xxs'),
    pl: s('sm'),
    pr: s('xs'),
  } as const;
  const destructiveActionButtonSx = { width: { sm: '152px' } } as const;

  const [wipeOpen, setWipeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const wipeForm = useDialogFormState({ projectName: '' });
  const deleteForm = useDialogFormState({ projectName: '' });
  const wipeErrors = useFormErrors(wipeForm);
  const deleteErrors = useFormErrors(deleteForm);
  const expectedProjectName = project?.name.trim() ?? '';
  const expectedWipeConfirmation = project ? `WIPE_PROJECT_DATA:${project.id}` : '';
  const wipeProjectNameMatches =
    expectedProjectName.length > 0 && wipeForm.values.projectName.trim() === expectedProjectName;
  const deleteProjectNameMatches =
    expectedProjectName.length > 0 && deleteForm.values.projectName.trim() === expectedProjectName;

  const closeWipeDialog = (): void => {
    if (wipeProjectData.isPending || deleteProject.isPending) return;
    wipeForm.handleClose(
      () => {
        setWipeOpen(false);
      },
      { clearError: mutationError.clearError },
    );
  };

  const closeDeleteDialog = (): void => {
    if (deleteProject.isPending || wipeProjectData.isPending) return;
    deleteForm.handleClose(
      () => {
        setDeleteOpen(false);
      },
      { clearError: mutationError.clearError },
    );
  };

  const confirmExpectedProjectName = (form: typeof wipeForm | typeof deleteForm): boolean => {
    const valid = form.validateRequired('projectName');
    if (!valid) return false;
    if (form.values.projectName.trim() === expectedProjectName) return true;
    form.setFieldError('projectName', 'Enter the exact project name to confirm.');
    return false;
  };

  return (
    <Panel title="Project Deletion" sx={settingsPanelSurfaceSx}>
      <Stack gap="sm">
        <Stack gap="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} gap="sm" align={{ sm: 'center' }}>
            <Text role="body" color="textSecondary" sx={[settingsCopyTextSx, { flex: 1 }]}>
              Delete all data associated with this project.
            </Text>
            <Button
              variant="destructive"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || wipeProjectData.isPending || deleteProject.isPending}
              onClick={() => {
                mutationError.clearError();
                wipeForm.resetDialog();
                setWipeOpen(true);
              }}
            >
              Delete Data
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap="sm" align={{ sm: 'center' }}>
            <Text role="body" color="textSecondary" sx={[settingsCopyTextSx, { flex: 1 }]}>
              Removes the project from your workspace.
            </Text>
            <Button
              variant="destructive"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || deleteProject.isPending || wipeProjectData.isPending}
              onClick={() => {
                mutationError.clearError();
                deleteForm.resetDialog();
                setDeleteOpen(true);
              }}
            >
              Delete Project
            </Button>
          </Stack>
        </Stack>
        <ConfirmDialog
          open={wipeOpen}
          title="Delete Project Data?"
          description={
            <Stack gap="md">
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                This action is permanent and cannot be undone. It will remove all project test data.
              </Text>
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Enter{' '}
                <Box component="code" sx={projectNameCodeSx}>
                  {expectedProjectName}
                </Box>{' '}
                to confirm.
              </Text>
              <TextField
                label="Project Name"
                fieldStyle="settings"
                value={wipeForm.values.projectName}
                onChange={wipeForm.handleChange('projectName')}
                {...wipeErrors.fieldErrorProps('projectName')}
                fullWidth
              />
            </Stack>
          }
          confirmLabel="Delete Data"
          confirmColor="error"
          confirmDisabled={!wipeProjectNameMatches}
          loading={wipeProjectData.isPending || deleteProject.isPending}
          onClose={closeWipeDialog}
          onConfirm={() => {
            if (!project || !confirmExpectedProjectName(wipeForm)) return;
            wipeProjectData.mutate(
              {
                id: project.id,
                confirmation: expectedWipeConfirmation,
                reasonCode: 'USER_REQUEST',
              },
              {
                onSuccess: (result) => {
                  mutationError.clearError();
                  wipeForm.resetDialog();
                  setWipeOpen(false);
                  if (result.status === 'scheduled') {
                    notify({ message: `Scheduled data wipe for ${project.name}`, tone: 'blue' });
                    return;
                  }
                  notify({ message: `Wiped data for ${project.name}`, tone: 'green' });
                },
                onError: (err) => {
                  mutationError.setError(err);
                },
              },
            );
          }}
        />
        <ConfirmDialog
          open={deleteOpen}
          title="Delete Project?"
          description={
            <Stack gap="md">
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                This action is permanent and cannot be undone.
              </Text>
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Enter{' '}
                <Box component="code" sx={projectNameCodeSx}>
                  {expectedProjectName}
                </Box>{' '}
                to confirm.
              </Text>
              <TextField
                label="Project Name"
                fieldStyle="settings"
                value={deleteForm.values.projectName}
                onChange={deleteForm.handleChange('projectName')}
                {...deleteErrors.fieldErrorProps('projectName')}
                fullWidth
              />
            </Stack>
          }
          confirmLabel="Delete Project"
          confirmColor="error"
          confirmDisabled={!deleteProjectNameMatches}
          loading={deleteProject.isPending || wipeProjectData.isPending}
          onClose={closeDeleteDialog}
          onConfirm={() => {
            if (!project || !confirmExpectedProjectName(deleteForm)) return;
            deleteProject.mutate(
              { id: project.id },
              {
                onSuccess: () => {
                  mutationError.clearError();
                  deleteForm.resetDialog();
                  setDeleteOpen(false);
                  notify({ message: `Deleted project ${project.name}`, tone: 'green' });
                  onProjectDeleted();
                },
                onError: (err) => {
                  mutationError.setError(err);
                },
              },
            );
          }}
        />
      </Stack>
    </Panel>
  );
};
